import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { formatNumber } from "../../utils";
import { COMPACT_QUERY } from "../../utils/hooks/useCompact";

const compact = "@media " + COMPACT_QUERY;

interface BillTotalsProps {
  /** Sum of the line totals at pricebook prices, before any bill-level discount. */
  totalAmount: string | number | null | undefined;
  discountAmount: string | number | null | undefined;
  /** Ex-VAT base the tax is charged on — after the discount, whatever the tax type. */
  totalNoTax: string | number | null | undefined;
  taxRate: string | number | null | undefined;
  taxAmount: string | number | null | undefined;
  grandTotal: string | number | null | undefined;
}

const Row = ({
  label,
  value,
  bold,
}: {
  label: string;
  value: string;
  bold?: boolean;
}) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      gap: 6,
      [compact]: { gap: 3 },
    }}
  >
    <Typography
      variant={bold ? "h6" : "body1"}
      color="primary"
      fontWeight={bold ? 600 : 500}
    >
      {label}
    </Typography>
    <Typography
      variant={bold ? "h6" : "body1"}
      color="primary"
      fontWeight={bold ? 600 : 500}
    >
      {value} บาท
    </Typography>
  </Box>
);

/**
 * The five figures the printed slip shows, in the printed order and under the
 * printed labels — รวมเงิน / ส่วนลด / จำนวนเงินก่อนภาษี / มูลค่าภาษี / ยอดรวมสุทธิ.
 *
 * Shared by the cash-sale and invoice detail screens. Before this, both showed the
 * grand total alone, which for a shop on a bill-footer discount meant a total that
 * was visibly lower than its own line items with nothing on the page to explain the
 * gap. Back office reconciles against the paper the shop is holding, so the screen
 * matches the paper row for row — the label carries no percentage because the slip
 * carries none, and on the bills with no discount ส่วนลด simply reads 0.00, again
 * exactly as it prints.
 */
export default function BillTotals({
  totalAmount,
  discountAmount,
  totalNoTax,
  taxRate,
  taxAmount,
  grandTotal,
}: BillTotalsProps) {
  const parsedTaxRate = Number(taxRate);
  const taxLabel = Number.isFinite(parsedTaxRate)
    ? `มูลค่าภาษี (${formatNumber(parsedTaxRate, { decimals: 0 })}%)`
    : "มูลค่าภาษี";

  return (
    <Box
      sx={{
        width: "fit-content",
        minWidth: 360,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        padding: "24px",
        margin: "24px 0 24px auto",
        bgcolor: "#5C69E836",
        borderRadius: 3,
        [compact]: {
          minWidth: 0,
          padding: "16px",
          margin: "16px 0 16px auto",
        },
      }}
    >
      <Row label="รวมเงิน" value={formatNumber(totalAmount)} />
      <Row label="ส่วนลด" value={formatNumber(discountAmount)} />
      <Row label="จำนวนเงินก่อนภาษี" value={formatNumber(totalNoTax)} />
      <Row label={taxLabel} value={formatNumber(taxAmount)} />
      <Divider sx={{ my: 0.5 }} />
      <Row label="ยอดรวมสุทธิ" value={formatNumber(grandTotal)} bold />
    </Box>
  );
}
